import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  arrayUnion,
  serverTimestamp
} from 'firebase/firestore';
import { storage, db } from '../config/firebase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { DEMO_PHOTOS } from '../contexts/DemoContext';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * Photo Service
 * Handles photo uploads, storage, and Dawson 21 categorization
 */

export const uploadPhoto = async (
  practiceId, userId, patientId, caseId, phaseId, file, category, metadata = {}
) => {
  if (IS_DEMO) {
    const id = 'demo-photo-' + Date.now();
    return {
      id,
      filename: file.name,
      storagePath: `demo/${file.name}`,
      downloadURL: URL.createObjectURL(file),
      category,
      patientId, caseId, phaseId,
      uploadedBy: userId,
      uploadedAt: new Date(),
      fileSize: file.size,
      fileType: file.type,
      ...metadata
    };
  }

  try {
    const timestamp = Date.now();
    const filename = `${category}_${timestamp}_${file.name}`;
    const storagePath = `practices/${practiceId}/patients/${patientId}/cases/${caseId}/phases/${phaseId}/${filename}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: { category, patientId, caseId, phaseId, uploadedBy: userId }
    });
    const downloadURL = await getDownloadURL(storageRef);
    const photoDoc = {
      filename, storagePath, downloadURL, category,
      patientId, caseId, phaseId, practiceId,
      uploadedBy: userId, uploadedAt: serverTimestamp(),
      fileSize: file.size, fileType: file.type,
      ...metadata
    };
    const photosRef = collection(
      db, 'practices', practiceId, 'patients', patientId,
      'cases', caseId, 'phases', phaseId, 'photos'
    );
    const photoDocRef = await addDoc(photosRef, photoDoc);
    const phaseRef = doc(
      db, 'practices', practiceId, 'patients', patientId,
      'cases', caseId, 'phases', phaseId
    );
    await updateDoc(phaseRef, {
      photos: arrayUnion(photoDocRef.id),
      updatedAt: serverTimestamp()
    });
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.CREATE,
      resourceType: 'photo',
      resourceId: photoDocRef.id,
      details: { category, caseId, phaseId }
    });
    return { id: photoDocRef.id, ...photoDoc };
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw error;
  }
};

export const getPhasePhotos = async (practiceId, patientId, caseId, phaseId) => {
  if (IS_DEMO) {
    return [...(DEMO_PHOTOS[phaseId] || [])];
  }

  try {
    const photosRef = collection(
      db, 'practices', practiceId, 'patients', patientId,
      'cases', caseId, 'phases', phaseId, 'photos'
    );
    const querySnapshot = await getDocs(photosRef);
    const photos = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return photos.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
  } catch (error) {
    console.error('Error getting photos:', error);
    throw error;
  }
};

export const getPhoto = async (practiceId, patientId, caseId, phaseId, photoId) => {
  if (IS_DEMO) {
    const photos = DEMO_PHOTOS[phaseId] || [];
    const found = photos.find(p => p.id === photoId);
    if (!found) throw new Error('Photo not found');
    return { ...found };
  }

  try {
    const photoRef = doc(
      db, 'practices', practiceId, 'patients', patientId,
      'cases', caseId, 'phases', phaseId, 'photos', photoId
    );
    const photoSnap = await getDoc(photoRef);
    if (!photoSnap.exists()) throw new Error('Photo not found');
    return { id: photoSnap.id, ...photoSnap.data() };
  } catch (error) {
    console.error('Error getting photo:', error);
    throw error;
  }
};

export const deletePhoto = async (
  practiceId, userId, patientId, caseId, phaseId, photoId, storagePath
) => {
  if (IS_DEMO) return true;

  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    const photoRef = doc(
      db, 'practices', practiceId, 'patients', patientId,
      'cases', caseId, 'phases', phaseId, 'photos', photoId
    );
    await deleteDoc(photoRef);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.DELETE,
      resourceType: 'photo',
      resourceId: photoId,
      details: { caseId, phaseId }
    });
    return true;
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

export const updatePhoto = async (
  practiceId, userId, patientId, caseId, phaseId, photoId, updates
) => {
  if (IS_DEMO) {
    return { id: photoId, ...updates, updatedAt: new Date() };
  }

  try {
    const photoRef = doc(
      db, 'practices', practiceId, 'patients', patientId,
      'cases', caseId, 'phases', phaseId, 'photos', photoId
    );
    const updateData = { ...updates, updatedAt: serverTimestamp() };
    await updateDoc(photoRef, updateData);
    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.UPDATE,
      resourceType: 'photo',
      resourceId: photoId,
      details: { caseId, phaseId }
    });
    return { id: photoId, ...updateData };
  } catch (error) {
    console.error('Error updating photo:', error);
    throw error;
  }
};

export default {
  uploadPhoto,
  getPhasePhotos,
  getPhoto,
  deletePhoto,
  updatePhoto
};
